import { CellProps, Column } from "react-table"
import { css, cx } from "@emotion/css"

import { ColorMap } from "utils/color"
import { MarketTax } from "models/taxes"
import MoreActions from "components/MoreActions"
import React from "react"
import { Status } from "components/Status"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

type ColumnState = Array<
  Column<MarketTax> & {
    header: string
    fixed?: boolean
    default: boolean
    variant?: string
    sortable?: boolean
  }
>

const commonColumns: Array<Column<MarketTax>> = [
  {
    fixed: true,
    default: true,
    sortable: false,
    header: "client.taxes.list.label",
    id: "label",
    accessor: "label",
    Cell: function (props: CellProps<MarketTax>) {
      const { label, isDefault } = props.row.original
      return (
        <div className={styles.wrapper}>
          <div className={"BodyBody-2BlackRegular"}>{label}</div>
          {isDefault && (
            <div className={cx("BodyBody-2Dark-greyRegular", styles.default)}>
              Default
            </div>
          )}
        </div>
      )
    },
  },
  {
    default: true,
    sortable: false,
    header: "client.taxes.list.semantic",
    id: "semantic",
    accessor: "semantic",
  },
  {
    default: true,
    sortable: false,
    header: "client.taxes.list.taxType",
    id: "taxType",
    accessor: "taxType",
  },
  {
    default: true,
    sortable: false,
    header: "client.taxes.list.erpCode",
    id: "erpCode",
    accessor: "erpCode",
  },
  {
    default: true,
    sortable: false,
    header: "client.taxes.list.amount",
    id: "amount",
    accessor: "amount",
    Cell: function (props: CellProps<MarketTax>) {
      const { value: taxAmount } = props

      return (
        <div className={"BodyBody-2BlackRegular"}>
          {`${taxAmount as string} %`}
        </div>
      )
    },
  },
  {
    default: true,
    sortable: false,
    header: "client.taxes.list.status",
    id: "status",
    accessor: "isEnabled",
    Cell: function (props: CellProps<MarketTax>) {
      const { value: isEnabled } = props
      const { t } = useI18n()
      const status = isEnabled ? "ACTIVE" : "INACTIVE"
      return (
        <Status color={ColorMap[status]}>{t(`taxStatus.${status}`)}</Status>
      )
    },
  },
]

const actionColumn = (
  t: (k: string) => string,
  clientId: string,
  onSetDefault: (taxId: string, clientId: string) => void,
  onEnable: (taxId: string, clientId: string) => void,
  onDisable: (taxId: string, clientId: string) => void,
): ColumnState => [
  {
    id: "actions",
    fixed: true,
    default: true,
    sortable: false,
    width: "80px",
    align: "center",
    header: "columns.actions",
    Cell: function (props: CellProps<MarketTax>) {
      const { hasAuthority } = useAuthenticated()
      const { id: taxId, isEnabled, isDefault } = props.row.original

      return (
        <MoreActions
          options={[
            // SET AS DEFAULT
            {
              hidden:
                !hasAuthority(["AUTH_EDIT_CLIENT_TAX"]) ||
                isDefault ||
                !isEnabled,
              label: t("client.taxes.list.set-default.action"),
              onClick: () => {
                onSetDefault(taxId, clientId)
              },
            },
            // MAKE AVAILABLE
            {
              hidden: !hasAuthority(["AUTH_EDIT_CLIENT_TAX"]) || isEnabled,
              label: t("client.taxes.list.enable.action"),
              onClick: () => {
                onEnable(taxId, clientId)
              },
            },
            // MAKE UNAVAILABLE
            {
              hidden:
                !hasAuthority(["AUTH_EDIT_CLIENT_TAX"]) ||
                !isEnabled ||
                isDefault,
              label: t("client.taxes.list.disable.action"),
              onClick: () => {
                onDisable(taxId, clientId)
              },
            },
          ]}
        />
      )
    },
  },
]

export const columns = (
  actionAllowed: boolean,
  t: (k: string) => string,
  clientId: string,
  onEnable: (taxId: string, clientId: string) => void,
  onDisable: (taxId: string, clientId: string) => void,
  onSetDefault: (taxId: string, clientId: string) => void,
) =>
  actionAllowed
    ? [
        ...commonColumns,
        ...actionColumn(t, clientId, onSetDefault, onEnable, onDisable),
      ]
    : commonColumns

const styles = {
  default: css`
    padding-left: var(--spacing-xxs);
  `,
  wrapper: css`
    display: flex;
    flex-direction: row;
  `,
}
